import type { Giveback2021BaseParagraphProps } from './Giveback2021Paragraph';

export interface Giveback2021Section {
  title: string;
  range: [number, number];
  flavor: 'dark' | 'light' | 'semi-dark';
  timelineFlavor: 'dark' | 'light' | 'semi-dark';
  position: number;
  items: Giveback2021BaseParagraphProps[];
}

export const GIVEBACK_2021_TEXTS_WITH_SECTIONS: Giveback2021Section[] = [
  {
    title: 'environment',
    flavor: 'dark',
    timelineFlavor: 'dark',
    range: [0.049, 0.075],
    position: 3794,
    items: [
      {
        title: '181,559 trees',
        subtitle: '\nplanted in critical ecosystems',
        organization: 'American Forests',
        amount: '$118,559',
      },
      {
        title: '50,000 pounds of litter',
        subtitle: 'cleared from beaches across America',
        organization: 'Alliance for The Great Lakes',
        amount: '$16,787',
      },
      {
        title: '3 coastal areas',
        subtitle: 'supported in Spain',
        organization: 'Surfrider Foundation Europe',
        amount: '$1,424',
      },
      {
        title: '9,052 nectar plants',
        subtitle: 'planted to restore butterfly habitats',
        organization: 'San Bruno Mountain Watch',
        amount: '$45,262',
      },
      {
        title: '256 trees',
        subtitle: 'planted\nin the Netherlands\nand Ghana',
        organization: 'Trees for All',
        amount: '$1,561',
      },
      {
        title: '375 youth climate leaders',
        subtitle: 'educated in fossil finance training',
        organization: '350.org',
        amount: '$71,064',
      },
      {
        title: '80 acres of native wetlands',
        subtitle: 'restored around the SF Bay',
        organization: 'Save The Bay',
        amount: '$5,590',
      },
      {
        title: '90 million kids',
        subtitle: 'given greater access to ocean education',
        organization: 'The Ocean Agency',
        amount: '$20,898',
      },
    ],
  },
  {
    title: 'animal rights',
    flavor: 'dark',
    timelineFlavor: 'dark',
    range: [0.1365, 0.174],
    position: 10747,
    items: [
      {
        title: '29,578 rescued animals',
        subtitle: 'given care and shelter',
        organization: 'SPCAI',
        amount: '$295,785',
      },
      {
        title: '1,291 dogs & cats',
        subtitle: 'rescued from cruelty',
        organization: 'The Humane Society',
        amount: '$64,556',
      },
      {
        title: '1,000 food packages',
        subtitle: 'delivered to endangered monkeys',
        organization: 'Pro Wildlife',
        amount: '$2,141',
      },
      {
        title: '17 rescued puppies',
        subtitle: 'vaccinated & cared for in shelters',
        organization: 'Deutscher Tierschutzbund',
        amount: '$1,197',
      },
      {
        title: '1 chimpanzee rehabilitation project',
        subtitle: 'sponsored in the River Gambia National Park',
        organization: 'Friends of Animals',
        amount: '$801',
      },
    ],
  },
  {
    title: 'global poverty',
    flavor: 'dark',
    timelineFlavor: 'dark',
    range: [0.236, 0.256],
    position: 18976,
    items: [
      {
        title: '12 clean water projects',
        subtitle: 'built in Malawi serving 2,400 people',
        organization: 'charity: water',
        amount: '$117,166',
      },
      {
        title: '500 people',
        subtitle: 'supplied with clean drinking water in Ethiopia',
        organization: 'Viva con Agua',
        amount: '$10,890',
      },
      {
        title: '65,659 lunches',
        subtitle: 'served to those facing hunger in the U.S.',
        organization: 'Feeding America',
        amount: '$65,659',
      },
      {
        title: '7,638 meals',
        subtitle: 'delivered to homebound elderly New Yorkers',
        organization: 'Citymeals on Wheels',
        amount: '$65,381',
      },
      {
        title: '7,055 Brazilian students',
        subtitle: 'returned safely to school following COVID-19',
        organization: 'Malala Fund',
        amount: '$53,042',
      },
      {
        title: '4,872 meals',
        subtitle: 'rescued for people in need',
        organization: 'Tafel Deutschland',
        amount: '$590',
      },
      {
        title: '200 people',
        subtitle: 'provided with homes',
        organization: 'New Story',
        amount: '$124,487',
      },
      {
        title: '22 food packages',
        subtitle: 'sent to malnourished kids in Mozambique',
        organization: 'Deutsche AIDS-Stiftung',
        amount: '$191',
      },
      {
        title: '3,400 families',
        subtitle: 'given safe housing\nin underserved communities in the U.S.',
        organization: 'Habitat for Humanity',
        amount: '$29,393',
      },
      {
        title: '4 weeks of therapeutic food',
        subtitle: 'supplied to children in crisis areas',
        organization: 'Ärzte ohne Grenzen',
        amount: '$309',
      },
    ],
  },
  {
    title: 'education',
    flavor: 'light',
    timelineFlavor: 'light',
    range: [0.367, 0.418],
    position: 29794,
    items: [
      {
        title: '200 hours of coding courses',
        subtitle: 'provided for kids',
        organization: 'Start Coding',
        amount: '$2,521',
      },
      {
        title: '5,450 teachers',
        subtitle: 'trained in high-need schools across the U.S.',
        organization: 'Teach For America',
        amount: '$43,543',
      },
      {
        title: '2,300 homeless youth',
        subtitle: 'taught STEM\nprogramming',
        organization: 'Women in Need',
        amount: '$112,007',
      },
      {
        title: '278 days of tutoring',
        subtitle: 'given to children in underfunded schools',
        organization: 'City Year',
        amount: '$1,388',
      },
      {
        title: '100 young Dutch people',
        subtitle: 'educated in environmental practice',
        organization: 'Fairtrade Netherlands',
        amount: '$504',
      },
      {
        title: '80 laptops',
        subtitle: 'bought for female Afghan engineering students',
        organization: 'Code to Inspire',
        amount: '$39,223',
      },
      {
        title: '60 hours of job readiness classes',
        subtitle: 'taught to refugee students in NYC',
        organization: 'Emma’s Torch',
        amount: '$10,490',
      },
      {
        title: '75 disadvantaged children',
        subtitle: 'trained in computer skills',
        organization: 'ReDI',
        amount: '$915',
      },
      {
        title: '10 schools',
        subtitle: 'provided with clean drinking water in Ghana, Guatemala, and Laos',
        organization: 'Pencils for Promise',
        amount: '$46,317',
      },
      {
        title: '10 laptops',
        subtitle: 'donated to underserved Dutch residents to help them re-enter society',
        organization: 'De Regenboog Groep',
        amount: '$1,872',
      },
      {
        title: '10 students',
        subtitle: 'funded for basic literacy training and test preparation',
        organization: 'Robin Hood',
        amount: '$28,050',
      },
      {
        title: '5 IDD individuals',
        subtitle: 'trained to be financially independent',
        organization: 'Best Buddies',
        amount: '$32,555',
      },
      {
        title: '1 STEM summer camp',
        subtitle: 'funded for Israeli and Palestinian students',
        organization: 'MEET',
        amount: '$57,042',
      },
      {
        title: '130 STEM resource kits',
        subtitle: 'provided to youth in foster care',
        organization: 'Together We Rise',
        amount: '$8,454.00',
      },
    ],
  },
  {
    title: 'civil rights',
    flavor: 'semi-dark',
    timelineFlavor: 'semi-dark',
    range: [0.541, 0.573],
    position: 38423,
    items: [
      {
        title: '1 Racial Justice Program',
        subtitle: 'supported to uphold racial equality',
        organization: 'ACLU',
        amount: '$89,264',
      },
      {
        title: '215 files of abuse material',
        subtitle: 'removed from the internet to protect children',
        organization: 'Thorn',
        amount: '$57,136',
      },
      {
        title: '74 trafficking survivors',
        subtitle: 'received trauma counseling sessions',
        organization: 'A21',
        amount: '$5,593',
      },
      {
        title: '7 high school students',
        subtitle: 'trained in gun violence prevention',
        organization: 'March For Our Lives',
        amount: '$16,552',
      },
      {
        title: '1 ‘Pay Your Workers’ campaign',
        subtitle: "funded to protect garment workers' rights",
        organization: 'Clean Clothes Campaign',
        amount: '$2,224',
      },
      {
        title: '1 journalist',
        subtitle: 'reimbursed for medical fees',
        organization: 'Reporter ohne Grenzen',
        amount: '$478',
      },
    ],
  },
  {
    title: 'mental health support',
    flavor: 'light',
    timelineFlavor: 'light',
    range: [0.626, 0.655],
    position: 45050,
    items: [
      {
        title: '240,000 people',
        subtitle: 'granted free online and reduced cost in-person mental health services',
        organization: 'TWLOHA',
        amount: '$63,930',
      },
      {
        title: '17,533 veterans',
        subtitle: 'supported with mental health resources',
        organization: 'Objective Zero',
        amount: '$10,234',
      },
      {
        title: '9,000 victims',
        subtitle: 'provided with high quality addiction treatment',
        organization: 'Shatterproof',
        amount: '$6,937',
      },
      {
        title: '4,113 emergency responses',
        subtitle: 'funded for those in a mental health crisis across the U.S.',
        organization: 'Crisis Text Line',
        amount: '$42,483',
      },
      {
        title: '530 schools',
        subtitle: 'trained in teen mental health first aid',
        organization: 'The Born This Way Foundation',
        amount: '$35,973',
      },
      {
        title: '145 therapy sessions',
        subtitle: 'provided for children recovering from abuse and neglect',
        organization: 'NYSPCC',
        amount: '$9,448',
      },
    ],
  },
  {
    title: 'healthcare',
    flavor: 'dark',
    timelineFlavor: 'dark',
    range: [0.752, 0.768],
    position: 53250,
    items: [
      {
        title: '900 blood donors',
        subtitle: 'matched for sickle cell disease',
        organization: 'The American Red Cross',
        amount: '$88,114',
      },
      {
        title: '1,601 medicine kits',
        subtitle: 'delivered to\nAfghanistan',
        organization: 'CARE',
        amount: '$1,601',
      },
      {
        title: '91,533 doses of COVID-19 medication',
        subtitle: 'sent to minority American communities',
        organization: 'Direct Relief',
        amount: '$66,025',
      },
      {
        title: '218 child cancer patients',
        subtitle: 'provided with gene sequencing\ntreatment',
        organization: 'CURE Childhood Cancer',
        amount: '$109,034',
      },
      {
        title: '45 stem cell donors',
        subtitle: 'added to the German National Bone Marrow Donor Registry',
        organization: 'DKMS',
        amount: '$1,919',
      },
      {
        title: '35 mammograms',
        subtitle: 'administered to breast cancer patients',
        organization: 'The National Breast Cancer Foundation',
        amount: '$32,152',
      },
      {
        title: '5 cholera victims',
        subtitle: 'given first aid in Yemen',
        organization: 'Deutsches Rotes Kreuz',
        amount: '$118',
      },
      {
        title: '1 #DonateDosesNow campaign',
        subtitle: 'supported for fair vaccine distribution',
        organization: 'UNICEF',
        amount: '$809',
      },
      {
        title: '30 cancer patients',
        subtitle: 'treated with\nphysiotherapy',
        organization: 'La Ligue Contre Le Cancer',
        amount: '$1,760',
      },
      {
        title: '10 medical staff',
        subtitle: 'supported with a relaxation area in ICHU',
        organization: 'Hôpitaux de France',
        amount: '$2,276',
      },
      {
        title: '1 rare cancer trial',
        subtitle: 'researched at  Memorial Sloan Kettering',
        organization: 'Cycle For Survival',
        amount: '$17,539',
      },
    ],
  },
  {
    title: 'lgbtq+',
    flavor: 'dark',
    timelineFlavor: 'dark',
    range: [0.869, 0.91],
    position: 62261,
    items: [
      {
        title: '218,000 meals\n',
        subtitle: 'donated to homeless LGBTQ+ youth',
        organization: 'The Ali Forney Center',
        amount: '$33,804',
      },
      {
        title: '1,600 LGBTQ+ youth\nin crisis',
        subtitle: 'provided with 24/7 counseling',
        organization: 'The Trevor Project',
        amount: '$104,358',
      },
      {
        title: '286 pounds of food',
        subtitle: 'distributed to the LGBTQ+ community in Burundi',
        organization: 'LSVD',
        amount: '$108',
      },
      {
        title: '60 nights of safe shelter',
        subtitle: 'given to LGBTQ+ youth',
        organization: 'Fondation Le Refuge',
        amount: '$503',
      },
    ],
  },
];
